<template>
    <Loading :loading="loading" class="min-h-screen w-full bg-[#f1f6fa] px-6 lg:px-20 lg:py-10 flex flex-col gap-6">
        <MarketsHeader v-if="currentProductGroup" :current-product-group="currentProductGroup" :to-overview="createPreviousURL()" />

        <div class="flex flex-col xl:flex-row justify-between m-2 xl:mt-0 gap-4">
            <button
                class="flex items-center gap-2 xl:gap-4 cursor-pointer rounded-lg bg-white w-full xl:w-fit px-3 py-2 text-sm hover:shadow-md hover:scale-105 duration-300 transition font-bold"
                @click="goBack"
            >
                <i class="fi fi-rr-arrow-left text-lg mt-2" />
                {{ $t('plain_back') }}
            </button>

            <Path :sticky="false" class="flex flex-wrap items-center gap-2 text-sm xl:text-base">
                <router-link to="/dashboard" class="text-blue-500 whitespace-nowrap">Dashboard</router-link>
                <i class="flex fi fi-rr-angle-small-right"></i>
                <router-link to="/dashboard/markets" class="text-blue-500 whitespace-nowrap">{{ $t('plain_analysis') }}</router-link>
                <template v-if="analysis.product">
                    <i class="flex fi fi-rr-angle-small-right"></i>
                    <router-link v-if="createPreviousURL() !== ''" :to="createPreviousURL()" class="text-blue-500 cursor-pointer whitespace-nowrap">
                        {{ $translate(analysisName) }}
                    </router-link>
                </template>
                <template v-if="analysis.published_at">
                    <i class="flex fi fi-rr-angle-small-right"></i>
                    <p class="text-blue-500 whitespace-nowrap">{{ formatDateByRegion($moment(analysis.published_at)) }}</p>
                </template>
            </Path>
        </div>

        <!-- 2  columns-->
        <div class="min-h-screen w-full bg-[#f1f6fa] flex pt-4 flex-col xl:flex-row gap-6">
            <div class="flex flex-col w-full">
                <div class="flex flex-row justify-between gap-4"></div>
                <!--Logo with Name inside frame-->
                <div class="flex flex-col gap-6 p-3 bg-white rounded-xl">
                    <div class="flex flex-row items-center gap-4">
                        <img class="object-contain w-auto h-12 duration-200" :src="productImage" />
                        <div>
                            <p class="font-bold text-md">{{ analysisName.de }}</p>
                            <p class="pr-4 text-sm leading-4 text-dashboard-primary-grey line-clamp-3" v-html="productGroupShortDescription.de" />
                        </div>
                        <span
                            v-if="analysis.product"
                            class="bg-[#F1F6FA] border-0 rounded-xl p-2 ml-auto hover:scale-105 hover:shadow-md duration-300"
                            @click="toggleFavoriteReadStatus(analysis, true)"
                        >
                            <i v-if="!favoriteStatus" class="flex fi fi-rr-bookmark"></i>
                            <i v-else class="flex fi fi-sr-bookmark"></i>
                        </span>
                    </div>
                    <div v-if="videoSource">
                        <div v-if="videoSource.bunny" style="position: relative; padding-top: 56.25%">
                            <iframe
                                :src="videoSource.bunny"
                                loading="lazy"
                                style="border: 0; position: absolute; top: 0; height: 100%; width: 100%"
                                allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                                allowfullscreen="true"
                            ></iframe>
                        </div>
                        <videoPlayer v-else-if="videoSource.video" :source="videoSource.video" class="w-full h-full" />
                    </div>
                </div>
            </div>
            <div class="w-full flex flex-col gap-8 h-[600px]">
                <ContentstreamsListV2
                    v-if="productId"
                    :product-id="productId"
                    :init-filter="['short', 'webinar', 'wmu', 'dsu', 'dmu']"
                    :show-dropdown="false"
                ></ContentstreamsListV2>
            </div>
            <!--  <div class="w-full flex flex-col gap-8 h-[600px]">
                <ContentstreamsList
                    v-if="contentstreams.length"
                    :header="false"
                    :filter-header="false"
                    :search-header="false"
                    :date-header="true"
                    :data="contentstreams"
                    :product-group="productGroup"
                    :title="$t('more_analyses')"
                    :filter="'contenstream_category_webinar'"
                    class="h-[600px] overflow-hidden"
                />
            </div> -->
        </div>
        <div class="px-6 lg:px-20 py-5 bg-[#f1f6fa]">
            <Disclaimer />
        </div>
    </Loading>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { formatDateByRegion } from '../../../helpers.js';
import Disclaimer from '../../customer/Disclaimer.vue';
// import ContentstreamsList from '../ContentstreamsList.vue';
import { fetchData } from '../../../fetchData.js';
import Path from '../Path.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const id = route.params.id;
let userId = null;
const productId = ref(null);
const analysis = ref([]);
const analysisName = ref('');
const analysisContent = ref('');
const productImage = ref([]);
const productGroupShortDescription = ref('');
const contentstreams = ref([]);
const favoriteStatus = ref(false);
const videoSource = ref('');
const productGroup = ref('');
const loading = ref(true);
const currentProductGroup = ref(null);

const goBack = () => {
    const lastRoute = sessionStorage.getItem('lastRoute');

    if (lastRoute) {
        router.push(lastRoute);
    } else {
        router.push('/dashboard');
    }
};

const getMarketProductGroups = async () => {
    const { result = [] } = await fetchData(`/api/v2/product-groups/${productGroup.value.id}`);

    currentProductGroup.value = { products: { data: result.data?.products } };
};

const getPastAnalysis = (productID) => {
    contentstreams.value = store.getters['contentstreams/all'].dashboard.data
        .filter((item) => productID.includes(item.product_id))
        .sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
};

const toggleFavoriteReadStatus = async (contentstream, currentContentStream = false) => {
    function update() {
        const contentstreamFavorite = contentstream.read_status === null ? true : !contentstream.read_status.is_favorite;

        store.dispatch('contentstream_user/update', {
            userId,
            contentstreamId: contentstream.id,
            isFavorite: contentstreamFavorite
        });

        if (currentContentStream) {
            favoriteStatus.value = !favoriteStatus.value;
        } else {
            contentstream.read_status = { is_favorite: contentstreamFavorite };
        }
    }

    if (contentstream.read_status === null) {
        initReadStatus(contentstream.id);
    }

    setTimeout(() => {
        update();
    }, 100);
};

const initReadStatus = (contentstreamID) => {
    store.dispatch('contentstream_user/init', {
        contentstreamId: contentstreamID,
        isRead: true
    });
};

const createPreviousURL = () => {
    if (productGroup.value.productType === 'doubleProduct') {
        return `/dashboard/markets-double/${productGroup.value?.id}`;
    }

    if (productGroup.value.productType === 'bundleProduct') {
        return `/dashboard/markets-package/${productGroup.value?.id}`;
    }

    return `/dashboard/markets/${productGroup.value?.id}`;
};

const waitForContentLoading = (content, continueFunction = null, continueFunctions = []) => {
    const interval = setInterval(() => {
        if (store.getters['settings/isContentLoaded'](content)) {
            if (continueFunctions.length > 0) {
                waitForContentLoading(continueFunctions[0], continueFunction, continueFunctions.shift);
            } else if (continueFunction) {
                continueFunction();
            }

            clearInterval(interval);
        }
    }, 10);
};

const getContentstreamData = async (contentstreamId) => {
    if (contentstreamId === null) {
        return;
    }

    videoSource.value = null;
    store.dispatch('contentstream/show', contentstreamId).then(() => {
        analysis.value = store.getters['contentstream/get'];
        productImage.value = analysis.value.product.image.replace('.png', '-gradient.png');
        analysisName.value = analysis.value.product.name;
        productId.value = analysis.value.product.id;
        analysisContent.value = analysis.value.content[0].attributes.wysiwyg;
        productGroupShortDescription.value = analysis.value.product.productGroups.data[0].short_description;
        videoSource.value = analysis.value.content[0].attributes;

        productGroup.value = analysis.value.product?.productGroups?.data?.[0] || null;

        if (analysis.value.read_status === null) {
            initReadStatus();
        } else {
            favoriteStatus.value = analysis.value.read_status.is_favorite;
        }

        getMarketProductGroups();
        waitForContentLoading('contentstreams', () => {
            getPastAnalysis(productId.value);
        });

        loading.value = false;
    });
};

router.afterEach((to, from) => {
    if (to.params.id !== from.params.id && to.name === from.name) {
        loading.value = true;
        getContentstreamData(to.params.id);
    }
});

onMounted(async () => {
    await store.dispatch('user/user').then(() => {
        userId = store.getters['user/get'].id;
    });

    await getContentstreamData(id);
});
</script>
